import React, { useState, useEffect } from "react";
import { z } from "zod";
import { Modal } from "@/components/common/Modal";
import { Input } from "@/components/common/Input";
import { Button } from "@/components/common/Button";
import { User, CreateUserDTO, UpdateUserDTO, Role, Application } from "@/contracts/auth-api.types";
import { userService } from "@/services/userService";
import { roleService } from "@/services/roleService";
import { applicationService } from "@/services/applicationService";
import { useNotification } from "@/hooks/useNotification";
import { getErrorMessage } from "@/utils/formatters";

const createUserSchema = z.object({
  name: z.string().min(1, "Nome é obrigatório"),
  username: z.string().optional(),
  email: z.string().email("E-mail inválido"),
  password: z
    .string()
    .min(8, "A senha deve ter no mínimo 8 caracteres")
    .max(72, "A senha deve ter no máximo 72 caracteres"),
  application_id: z.string().optional(),
  is_verified: z.boolean().default(false),
});

const updateUserSchema = z.object({
  name: z.string().min(1, "Nome é obrigatório"),
  is_verified: z.boolean().default(false),
});

interface UserModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  userToEdit?: User | null;
}

export const UserModal: React.FC<UserModalProps> = ({ isOpen, onClose, onSuccess, userToEdit }) => {
  const { notify } = useNotification();
  const isEditing = !!userToEdit;

  const [formData, setFormData] = useState({
    name: "",
    username: "",
    email: "",
    password: "",
    application_id: "",
    is_verified: false,
  });

  const [selectedRoleIds, setSelectedRoleIds] = useState<string[]>([]);
  const [availableRoles, setAvailableRoles] = useState<Role[]>([]);
  const [applications, setApplications] = useState<Application[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const tenantRoles = React.useMemo(() => {
    if (!formData.application_id) {
      return availableRoles;
    }
    return availableRoles.filter(
      (r) => !r.application_id || r.application_id === formData.application_id,
    );
  }, [availableRoles, formData.application_id]);

  useEffect(() => {
    async function loadAuxiliaryData() {
      try {
        const [roles, appsRes] = await Promise.all([
          roleService.listRoles().catch(() => []),
          applicationService.list(1, 100).catch(() => []),
        ]);
        setAvailableRoles(roles || []);
        const appList = Array.isArray(appsRes) ? appsRes : appsRes.data || [];
        setApplications(appList);
      } catch {
        // Fallback silencioso
      }
    }

    if (isOpen) {
      void loadAuxiliaryData();
    }
  }, [isOpen]);

  useEffect(() => {
    if (userToEdit) {
      setFormData({
        name: userToEdit.name,
        username: userToEdit.username || "",
        email: userToEdit.email,
        password: "",
        application_id: userToEdit.application_id,
        is_verified: userToEdit.is_verified,
      });
      setSelectedRoleIds((userToEdit.roles || []).map((r) => r.id));
    } else {
      setFormData({
        name: "",
        username: "",
        email: "",
        password: "",
        application_id: "",
        is_verified: false,
      });
      setSelectedRoleIds([]);
    }
    setErrors({});
  }, [userToEdit, isOpen]);

  const toggleRole = (roleId: string) => {
    setSelectedRoleIds((prev) =>
      prev.includes(roleId) ? prev.filter((id) => id !== roleId) : [...prev, roleId],
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    if (isEditing) {
      const result = updateUserSchema.safeParse({
        name: formData.name,
        is_verified: formData.is_verified,
      });

      if (!result.success) {
        const fieldErrors: Record<string, string> = {};
        result.error.issues.forEach((issue) => {
          const field = issue.path[0] as string;
          if (!fieldErrors[field]) {
            fieldErrors[field] = issue.message;
          }
        });
        setErrors(fieldErrors);
        return;
      }

      setIsSubmitting(true);
      try {
        const payload: UpdateUserDTO = {
          name: formData.name,
          is_verified: formData.is_verified,
          role_ids: selectedRoleIds,
        };
        await userService.update(userToEdit!.id, payload);
        notify("Usuário atualizado com sucesso!", "success");
        onSuccess();
        onClose();
      } catch (err: unknown) {
        notify(getErrorMessage(err, "Erro ao atualizar o usuário"), "error");
      } finally {
        setIsSubmitting(false);
      }
    } else {
      const result = createUserSchema.safeParse(formData);
      if (!result.success) {
        const fieldErrors: Record<string, string> = {};
        result.error.issues.forEach((issue) => {
          const field = issue.path[0] as string;
          if (!fieldErrors[field]) {
            fieldErrors[field] = issue.message;
          }
        });
        setErrors(fieldErrors);
        return;
      }

      setIsSubmitting(true);
      try {
        const payload: CreateUserDTO = {
          name: formData.name,
          username: formData.username || undefined,
          email: formData.email,
          password: formData.password,
          application_id: formData.application_id || undefined,
          is_verified: formData.is_verified,
          role_ids: selectedRoleIds,
        };
        await userService.create(payload);
        notify("Usuário cadastrado com sucesso!", "success");
        onSuccess();
        onClose();
      } catch (err: unknown) {
        notify(getErrorMessage(err, "Erro ao cadastrar o usuário"), "error");
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEditing ? `Editar Usuário: ${userToEdit.name}` : "Cadastrar Novo Usuário"}
      maxWidth="md"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <Input
          id="user-name-input"
          label="Nome Completo"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          error={errors.name}
          placeholder="Ex: Ana Silva"
          required
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            id="user-email-input"
            type="email"
            label="E-mail"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            error={errors.email}
            placeholder="ana.silva@empresa.com"
            disabled={isEditing}
            required
          />

          <Input
            id="user-username-input"
            label="Nome de Usuário (Opcional)"
            value={formData.username}
            onChange={(e) => setFormData({ ...formData, username: e.target.value })}
            error={errors.username}
            placeholder="anasilva"
            disabled={isEditing}
          />
        </div>

        {!isEditing && (
          <Input
            id="user-password-input"
            type="password"
            label="Senha Inicial"
            value={formData.password}
            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
            error={errors.password}
            helperText="Mínimo de 8 e máximo de 72 caracteres (OWASP ASVS)"
            required
            autoComplete="new-password"
          />
        )}

        {!isEditing && applications.length > 0 && (
          <div className="input-group">
            <label htmlFor="user-app-select" className="input-label">
              Tenant / Aplicação Vinculada
            </label>
            <select
              id="user-app-select"
              className="input-control"
              value={formData.application_id}
              onChange={(e) => {
                const newAppId = e.target.value;
                setFormData((prev) => ({ ...prev, application_id: newAppId }));
                setSelectedRoleIds((prev) =>
                  prev.filter((id) => {
                    const role = availableRoles.find((r) => r.id === id);
                    return role && (!role.application_id || role.application_id === newAppId);
                  }),
                );
              }}
            >
              <option value="">Aplicação Atual (Default)</option>
              {applications.map((app) => (
                <option key={app.id} value={app.id}>
                  {app.name} ({app.client_id})
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Roles Selection */}
        <div className="space-y-2">
          <div className="flex justify-between items-center">
            <span className="input-label block">Perfis de Acesso (Roles RBAC)</span>
            {formData.application_id && (
              <span className="text-[11px] text-muted font-mono">
                Isolado por Tenant ({tenantRoles.length} disponíveis)
              </span>
            )}
          </div>
          {tenantRoles.length === 0 ? (
            <p className="text-xs text-muted p-2.5 bg-gray-50 rounded border border-gray-200">
              {formData.application_id
                ? "Nenhuma role cadastrada para este tenant. Crie roles no módulo Roles & Permissões."
                : "Nenhuma role cadastrada para seleção."}
            </p>
          ) : (
            <div className="grid grid-cols-2 gap-2 p-3 bg-gray-50 border border-gray-200 rounded-md max-h-40 overflow-y-auto">
              {tenantRoles.map((role) => (
                <label
                  key={role.id}
                  className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={selectedRoleIds.includes(role.id)}
                    onChange={() => toggleRole(role.id)}
                    className="rounded text-primary focus:ring-primary h-4 w-4"
                  />
                  <span>{role.name}</span>
                </label>
              ))}
            </div>
          )}
        </div>

        <div className="flex items-center gap-2 pt-2">
          <input
            type="checkbox"
            id="user-verified-checkbox"
            checked={formData.is_verified}
            onChange={(e) => setFormData({ ...formData, is_verified: e.target.checked })}
            className="rounded text-primary focus:ring-primary h-4 w-4"
          />
          <label htmlFor="user-verified-checkbox" className="text-sm font-medium text-gray-700">
            Usuário com E-mail Verificado
          </label>
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
          <Button type="button" variant="secondary" onClick={onClose} disabled={isSubmitting}>
            Cancelar
          </Button>
          <Button type="submit" variant="primary" isLoading={isSubmitting}>
            {isEditing ? "Salvar Alterações" : "Criar Usuário"}
          </Button>
        </div>
      </form>
    </Modal>
  );
};
